import {
  faCalendarDays,
  faCircleNodes,
  faBuilding,
  faClock,
  faDiagramProject,
  faFileContract,
  faFileInvoiceDollar,
  faFileShield,
  faFlask,
  faHandshake,
  faIdCard,
  faListCheck,
  faPeopleGroup,
  faUsers,
  type IconDefinition
} from '@fortawesome/free-solid-svg-icons';

import {
  VIEW_BILLING_REPORT_PERMISSIONS,
  VIEW_BILLING_REPORT_RESULTS_PERMISSIONS
} from '@/pages/BillingReportResults/constants';
import { UserPermissions } from '@/types/user';
import {
  BLUEPRINT_VIEW,
  CONTRACT_VIEW,
  ENGINEERING_GROUP,
  hasPermission,
  isInGroup,
  ORGANIZATION_VIEW,
  TREATABILITY_MATCH_VIEW,
  TREATMENT_GROUP_VIEW,
  VOB_EDITORS_GROUP
} from '@/utils/userPermission';

type WorkspacePermissionRule =
  | { type: 'all'; requiredPermissions: string[] }
  | { type: 'single'; requiredPermission: string }
  | { type: 'group'; groupName: string }
  | undefined;

export interface WorkspaceNavDefinition {
  label: string;
  path: string;
  icon: IconDefinition;
  /** Extra paths this entry owns; see `MatchableNavItem`. */
  alsoMatches?: string[];
  permissionRule?: WorkspacePermissionRule;
  permissionHint?: string;
}

export const WORKSPACE_NAV_DEFINITIONS: WorkspaceNavDefinition[] = [
  /*
   * A person is opened from the People list, so their pages count as being in it — and so does an
   * encounter, which opens inside the person it belongs to.
   */
  { label: 'People', path: '/', alsoMatches: ['/people', '/encounters'], icon: faUsers },
  { label: 'Tasks', path: '/tasks', icon: faListCheck },
  { label: 'My Circles', path: '/my-circles', icon: faCircleNodes },
  { label: 'Credentialing', path: '/credentialing/', icon: faIdCard },
  {
    label: 'Billing',
    path: '/billing/',
    icon: faFileInvoiceDollar,
    permissionRule: {
      type: 'all',
      requiredPermissions: [
        ...VIEW_BILLING_REPORT_RESULTS_PERMISSIONS,
        ...VIEW_BILLING_REPORT_PERMISSIONS
      ]
    },
    permissionHint: 'if permitted'
  },
  {
    label: 'Treatability Matches',
    path: '/treatability-matches',
    icon: faHandshake,
    permissionRule: { type: 'single', requiredPermission: TREATABILITY_MATCH_VIEW },
    permissionHint: 'if permitted'
  },
  {
    label: 'Treatment Groups',
    path: '/treatment-groups',
    icon: faPeopleGroup,
    permissionRule: { type: 'single', requiredPermission: TREATMENT_GROUP_VIEW },
    permissionHint: 'if permitted'
  },
  { label: 'Schedules', path: '/schedules', icon: faClock },
  { label: 'Calendar', path: '/calendar', icon: faCalendarDays },
  {
    label: 'Blueprints',
    path: '/blueprints',
    icon: faDiagramProject,
    permissionRule: { type: 'single', requiredPermission: BLUEPRINT_VIEW },
    permissionHint: 'if permitted'
  },
  {
    label: 'Organizations',
    path: '/organizations',
    icon: faBuilding,
    permissionRule: { type: 'single', requiredPermission: ORGANIZATION_VIEW },
    permissionHint: 'if permitted'
  },
  {
    label: 'Contracts',
    path: '/contracts',
    icon: faFileContract,
    permissionRule: { type: 'single', requiredPermission: CONTRACT_VIEW },
    permissionHint: 'if permitted'
  },
  {
    label: 'VOB List',
    path: '/vob/list',
    icon: faFileShield,
    permissionRule: { type: 'group', groupName: VOB_EDITORS_GROUP },
    permissionHint: 'if permitted'
  },
  {
    label: 'Engineering',
    path: '/eng/dashboard',
    icon: faFlask,
    permissionRule: { type: 'group', groupName: ENGINEERING_GROUP },
    permissionHint: 'if permitted'
  }
];

export const getAvailableWorkspaceNavOptions = (
  permissions: UserPermissions | undefined
): WorkspaceNavDefinition[] =>
  WORKSPACE_NAV_DEFINITIONS.filter((option) => {
    if (!option.permissionRule) {
      return true;
    }

    if (option.permissionRule.type === 'all') {
      return hasPermission({
        permissions,
        requiredPermissions: option.permissionRule.requiredPermissions
      });
    }

    if (option.permissionRule.type === 'group') {
      return isInGroup(permissions, option.permissionRule.groupName);
    }

    return hasPermission({
      permissions,
      requiredPermission: option.permissionRule.requiredPermission
    });
  });
